import * as React from 'react';
import { useState, useEffect } from "react";
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import './MultiDropdownStyles.css';
import { VariableSizeList } from 'react-window';
import { useMediaQuery, useTheme } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';

const LISTBOX_PADDING = 4;

function renderRow(props) {
    const { data, index, style } = props;
    const item = data[index];
    const inlineStyle = {
        ...style,
        top: style.top + LISTBOX_PADDING,
        width: "100%",
        boxSizing: "border-box",
        padding: "12px 1px",
        lineHeight: "1.2",
        whiteSpace: "normal",        // allow wrapping
        overflow: "visible",         // show wrapped lines
        textOverflow: "unset",
        wordBreak: "break-word",
        fontSize: "15px",
        fontWeight: "400"
    };
    return React.cloneElement(item, {
        style: inlineStyle,
    });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}

const StyledUl = React.forwardRef(({ style, ...props }, ref) => (
    <ul
        ref={ref}
        style={{
            ...style,
            width: '80%',
            listStyle: 'none',
            overflowY: 'auto',
            padding: 0,
            margin: 10,
        }}
        {...props}
    />
));

const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
    const { children, ...other } = props;
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;
    const itemSize = 48;
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'));

    const getChildSize = (child) => {
        const text = child?.props?.children?.[1] || "";
        const approxLines = Math.ceil(text.length / 20);
        return 52;
    };

    const gridRef = useResetCache(itemCount);

    return (
        <div ref={ref} style={{ width: '100%' }}>
            <OuterElementContext.Provider value={other} >
                <VariableSizeList
                    itemData={itemData}
                    height={200}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType={StyledUl}
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const MultiDropdown = ({ id, options, onChangeItems, onDropdownClose, label, selectedItemsData, dropdownWidth, dropdownHeight, name, disabled, width, height, fontSize = 16, copyPasteDisabled = true, ListboxComponentProps = true }) => {
    const [selectedItems, setSelectedItems] = useState(selectedItemsData || []);
    const [openModal, setOpenModal] = useState(false);
    const [selectAll, setSelectAll] = useState(false);
    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
        setSelectAll(selectedItems?.length === options?.length);
        setSelectedItems(Array.isArray(selectedItemsData) ? selectedItemsData : []);
    }, [selectedItems, options?.length, selectedItemsData]);

    const handleChange = (event, value) => {

        if (value?.some((item) => item?.title === "Select All")) {
            if (selectAll) {
                setSelectedItems([]);
                setSelectAll(false);
            } else {
                setSelectedItems(options);
                setSelectAll(true);
            }
        } else {
            setSelectedItems(value);
            setSelectAll(value?.length === options?.length);
        }

        onChangeItems(name, value);
    };

    const handleInputChange = (event, newInputValue, reason) => {
        // ignore the 'reset' reason (fired by option select) so typed text stays
        if (reason === 'reset') return;
        setInputValue(newInputValue);
    };

    const handleRemoveChip = (chipToRemove) => {
        const newSelectedItems = selectedItems?.filter(
            (item) => item.title !== chipToRemove?.title
        );
        setSelectedItems(newSelectedItems);
        setSelectAll(false);
        onChangeItems(name, newSelectedItems);
        if (typeof onDropdownClose === 'function') {
            onDropdownClose(name, newSelectedItems);
        }
    };

    const handleClickInside = () => {
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleSelectAllToggle = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);
        const newSelectedItems = newSelectAll ? options : [];
        setSelectedItems(newSelectedItems);
        onChangeItems(name, newSelectedItems);
        if (selectAll) {
            setSelectedItems([]);
        } else {
            setSelectedItems(options);
        }
        setSelectAll(!selectAll);
    };

    const handleCloseDropdown = () => {
        setSelectAll(false);
        setInputValue("");
        if (typeof onDropdownClose === 'function') {
            onDropdownClose(name, Array.isArray(selectedItems) ? selectedItems : []);
        }
    }
    const allOptions = options ? [{ title: "Select All", value: true }, ...options] : [];

    const handlePasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();

            const pastedValues = text
                .split(/[\n,]+/)
                .map(val => val.trim())
                .filter(Boolean);

            const matchedOptions = options?.filter(option => {
                const normalizedPastedValues = pastedValues.map(v =>
                    v.toLowerCase().trim()
                );

                const fullTitle = option.title.toLowerCase().trim();

                const [codePart, namePart] = option.title
                    .split('|')
                    .map(v => v.toLowerCase().trim());

                return normalizedPastedValues.some(pasted =>
                    pasted === codePart ||                 
                    pasted === namePart ||  
                    pasted === fullTitle  
                );
            });

            if (matchedOptions.length > 0) {
                setSelectedItems(matchedOptions);
                setSelectAll(matchedOptions.length === options.length);
                onChangeItems(name, matchedOptions);
            } else {
                console.log("No matching options found");
            }
        } catch (err) {
            console.error("Failed to read clipboard: ", err);
        }
    };

    return (
        <div>
            <Autocomplete
                ListboxComponent={ListboxComponentProps === true ? ListboxComponent : undefined}
                multiple={true}
                disabled={disabled || false}
                id={id || "checkboxes-tags-demo"}
                name={name || "autoComplete_name"}
                options={allOptions}
                disableCloseOnSelect
                value={Array.isArray(selectedItems) ? selectedItems : []}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                onChange={handleChange}
                onClose={handleCloseDropdown}
                isOptionEqualToValue={(option, value) => option?.title === value?.title}
                getOptionLabel={(option) => option?.title}
                filterOptions={(options, { inputValue }) => {
                    let filtered = options.filter(
                        option =>
                            option.title.toLowerCase().includes(inputValue.toLowerCase()) ||
                            (option.name && option.name.toLowerCase().includes(inputValue.toLowerCase()))
                    );

                    // if(!inputValue){
                    //     return filtered.slice(0,10);
                    // }
                    return filtered;
                }
                }
                renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    const isItemSelected = selectedItems?.some(item => item?.title === option?.title);
                    if (option?.title === 'Select All') {
                        return (
                            <li key={key} {...optionProps} onClick={handleSelectAllToggle}>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selectAll}
                                />
                                {option?.title}
                            </li>
                        );
                    }

                    return (
                        <li key={key} {...optionProps} style={{ fontSize: 16 }}>
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={isItemSelected}
                            />
                            {option?.title}
                        </li>
                    );
                }}
                style={{ maxWidth: dropdownWidth || "100%", height: height || 30 }}
                renderInput={(params) => <TextField {...params} disabled={disabled || false} label={label} placeholder={"Search"}
                    sx={{
                        width,
                        "& .MuiInputBase-root": {
                            height,
                            fontSize,
                            paddingRight: "12px", // room for dropdown icon
                        },
                        "& .MuiInputLabel-root": {
                            fontSize,
                        },
                        "& .MuiOutlinedInput-root": {
                            padding: "5px !important",
                            margin: "5px"
                        },
                        "& .MuiOutlinedInput-input": {

                        },
                    }}
                    onClick={() => {
                        setSelectAll(selectedItems?.length === options?.length)

                    }} />}
                renderTags={(selected, getTagProps) => {
                    if (selectedItemsData?.length > 0 && selectedItems?.length > 0) {
                        return (
                            <Box
                                onClick={handleClickInside}
                                sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", justifyContent: 'center' }}
                            >
                                <span
                                    style={{
                                        fontSize: 18,
                                        marginRight: 10,
                                        marginTop: 2
                                    }}
                                >{selected?.length}</span>
                            </Box>
                        )
                    } else {
                        return null;
                    }
                }}
            />
            {copyPasteDisabled === true && <DialogActions className='pasteClipboard'>
                <Button className='clipboardButton' onClick={handlePasteFromClipboard} color="secondary">Paste from Clipboard</Button>
            </DialogActions>}
            <Dialog open={openModal} onClose={handleCloseModal}>
                <DialogTitle>Selected Options ({selectedItems?.length})</DialogTitle>
                <DialogContent sx={{ maxHeight: "300px", overflowY: "auto" }}>
                    {selectedItems?.length > 0 ? (
                        selectedItems?.map((option, index) => (
                            disabled ? <Chip
                                key={index}
                                label={option.title}
                                sx={{ margin: "4px" }} />
                                : <Chip
                                    key={index}
                                    label={option.title}
                                    sx={{ margin: "4px" }}
                                    onDelete={() => handleRemoveChip(option)}
                                />
                        ))
                    ) : (
                        <p>No items selected</p>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="primary">Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
